import { Form, Input, Modal, notification, Typography } from 'antd';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import { Organization } from 'protocol/model/dto_organization_pb';

import { useAppDispatch } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

const { Title, Text } = Typography;

type MarkAsDistributorModalProps = {
  isOpen: boolean;
  onCloseHandler: () => void;
  organization: Organization;
};

type MarkAsDistributorFormFields = {
  distributorName: string;
};

export const MarkAsDistributorModal: FC<MarkAsDistributorModalProps> = (
  props,
) => {
  const { isOpen, onCloseHandler, organization } = props;

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const [loading, setLoading] = useState<boolean>(false);
  const [form] = Form.useForm<MarkAsDistributorFormFields>();

  const handleSubmit = () =>
    form.validateFields().then(async (values) => {
      const { distributorName } = values;

      try {
        setLoading(true);

        const { Organization: updatedOrganization } = await dispatch(
          userModel.thunks.markOrganizationAsDistributor({
            OrganizationID: organization.ID,
            DistributorName: distributorName,
          }),
        ).unwrap();

        if (updatedOrganization?.ID) {
          dispatch(
            organizationModel.actions.setNewestOrganization(updatedOrganization),
          );
        }

        notification.success({
          message: formatMessage({
            id: 'distributorModal.markSuccess',
            defaultMessage: 'Organization marked as distributor successfully',
          }),
        });

        form.resetFields();
        onCloseHandler();
      } catch (error) {
        notification.error({
          message: error?.message || formatMessage({
            id: 'global.error.somethingWentWrong',
            defaultMessage: 'Something went wrong',
          }),
        });
      } finally {
        setLoading(false);
      }
    });

  return (
    <Modal
      open={isOpen}
      centered
      closable={false}
      okText={formatMessage({ id: 'global.confirm', defaultMessage: 'Confirm' })}
      cancelText={formatMessage({
        id: 'global.cancel',
        defaultMessage: 'Cancel',
      })}
      onCancel={onCloseHandler}
      onOk={handleSubmit}
      confirmLoading={loading}
    >
      <Title level={3}>
        {formatMessage({
          id: 'distributorModal.markTitle',
          defaultMessage: 'Mark Organization as Distributor',
        })}
      </Title>

      <Text>
        {formatMessage({
          id: 'distributorModal.markDescription',
          defaultMessage:
            'This organization will be able to manage other organizations as clients',
        })}
      </Text>

      <Form
        name="markAsDistributorForm"
        form={form}
        layout="vertical"
        autoComplete="off"
        style={{ marginTop: 16 }}
      >
        <Form.Item
          name="distributorName"
          label={formatMessage({
            id: 'distributorModal.distributorNameLabel',
            defaultMessage: 'Distributor Name',
          })}
          rules={[
            {
              required: true,
              message: formatMessage({
                id: 'forms.required',
                defaultMessage: 'Field is required',
              }),
            },
          ]}
        >
          <Input
            placeholder={formatMessage({
              id: 'distributorModal.distributorNamePlaceholder',
              defaultMessage: 'Enter distributor name',
            })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
